import React from "react";
import { CheckCircle2, Pencil, Trash2 } from "lucide-react";
import { IUserAddress } from "muminsmart-utils";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

type AddressListDrawerProps = {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  addresses: IUserAddress[];
  selectedAddressId: string | null;
  isAddressMutating: boolean;
  onSelect: unknown;
  onEdit: unknown;
  onDelete: unknown;
  onAddNew: () => void;
};

export default function AddressListDrawer({
  open,
  onOpenChange,
  addresses,
  selectedAddressId,
  isAddressMutating,
  onSelect,
  onEdit,
  onDelete,
  onAddNew,
}: AddressListDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <Button variant="outline" className="rounded-full">
          Change Address
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mx-auto w-full max-w-xl">
          <DrawerHeader>
            <DrawerTitle>Select Delivery Address</DrawerTitle>
            <DrawerDescription>
              Choose an address for this order.
            </DrawerDescription>
          </DrawerHeader>

          <div className="no-scrollbar overflow-y-auto space-y-2 p-4">
            {addresses.map((address) => {
              const isSelected = selectedAddressId === address.id;

              return (
                <div
                  key={address.id}
                  className={`rounded-xl border p-3 ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-zinc-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">
                          {address.label || "Saved Address"}
                        </p>
                        {address.isDefault && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-zinc-800">
                        {address.fullName} · {address.phone}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {address.addressLine1}
                        {address.addressLine2
                          ? `, ${address.addressLine2}`
                          : ""}
                        , {address.city}, {address.state} - {address.pincode},{" "}
                        {address.country}
                      </p>
                    </div>
                    {isSelected ? (
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    ) : null}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant={isSelected ? "secondary" : "default"}
                      size="sm"
                      disabled={isAddressMutating}
                      onClick={() => {
                        if (typeof onSelect === "function") {
                          void onSelect(address);
                        }
                      }}
                    >
                      {isSelected ? "Selected" : "Select"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isAddressMutating}
                      onClick={() => {
                        if (typeof onEdit === "function") {
                          onEdit(address);
                        }
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-rose-600"
                      disabled={isAddressMutating}
                      onClick={() => {
                        if (typeof onDelete === "function") {
                          void onDelete(address.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <DrawerFooter>
            <Button
              className="rounded-full"
              onClick={onAddNew}
              disabled={isAddressMutating}
            >
              Add New Address
            </Button>
            <DrawerClose asChild>
              <Button className="rounded-full" variant="secondary">
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
